import React, { useEffect } from "react";
import { useState } from "react";
import {
  ANNUAL_EMISSIONS_HEADING_TEXT,
  EMISSIONS_AVERAGE_HEADING_TEXT,
  EMISSION_CATEGORIES_HEADING_TEXT,
  EMISSION_CATEGORIES_TABLE,
  GRAPH_INFO,
  INDUSTRY_AVERAGES,
  NITROGEN_FERTILISER_HEADING_TEXT,
  NITROGEN_FERTILISER_TABLE,
  RESULTS_TEXT,
  VIEW_RESULTS_LABEL_TEXT,
  VIEW_RESULTS_OPTIONS,
  EMISSIONS_EQUIVALENT_VALUES_INFO,
  FINAL_VALUES,
  RESULTS_KEY,
  OPEN_EMAIL_BUTTON_TEXT,
  EMISSIONS_PROPORTION_HEADING_TEXT,
  CALCULATED_RESULTS_KEY,
  FERTILISER_ANALYSIS_OF_TOTAL_EMISSION_HEADING_TEXT,
  FERTILISER_ANALYSIS_TABLE,
  EMISSIONS_AVERAGE_SUBHEADING_TEXT,
  ENERGY_ANALYSIS_TABLE,
  ENERGY_ANALYSIS_OF_TOTAL_EMISSION_HEADING_TEXT,
  PRINT_BUTTON_TEXT,
  VIEWS,
} from "../../constants";
import { DisplayTable, SelectInput } from "../../UIKit/components";
import {
  Heading2,
  Heading5,
  Heading6,
  PageContainer,
  PageHeadingWrapper,
  PageSection,
  PrimaryButton,
  PageBreak,
  ButtonWrapper,
  ParagraphText,
} from "../../UIKit/styles";
import {
  getSavedFieldValue,
  getSessionStorageValue,
  setSessionStorage,
  formatNumber,
  isNullOrUndefined,
  updateGraphLabel,
} from "../../utils/utils";
import {
  BarGraph,
  EmissionsDisplay,
  ErrorModal,
  LoadingModal,
  PieGraph,
} from "../../Components";
import { theme } from "../../UIKit";
import { useLocation, useNavigate } from "react-router";

export default function Results({
  data,
  setEmailModalOpen,
  recalculateResults,
  getUserBearerToken,
  setRenewOrchardData,
  setRecalculateResults,
}) {
  const [error, setError] = useState(null);

  const [isCalculating, setIsCalculating] = useState(false);
  const [viewResultsBy, setViewResultsBy] = useState(
    VIEW_RESULTS_OPTIONS[0].value
  );
  const [viewResultsByLabel, setViewResultsByLabel] = useState(
    VIEW_RESULTS_OPTIONS[0].value.charAt(3).toLowerCase() +
      VIEW_RESULTS_OPTIONS[0].value.slice(4)
  );

  const navigate = useNavigate();
  const { search } = useLocation();

  const getTitle = () => {
    const orchardName = getSavedFieldValue(
      "calculatorDetails",
      null,
      "orchardName",
      data
    );
    if (!orchardName) return RESULTS_TEXT;
    return `${RESULTS_TEXT} for ${orchardName}`;
  };

  const getErrorMessage = (error) => {
    const fieldName = VIEWS.find((view) => {
      if (view.sections) {
        return view.sections.find((section) => {
          if (section.inputFields) {
            return section.inputFields.find((field) => {
              return error.includes(field.field);
            });
          }
        });
      }
      return false;
    });
    if (fieldName) {
      return fieldName.title;
    }
    return false;
  };

  useEffect(() => {
    const generateResults = async () => {
      try {
        const searchParams = new URLSearchParams(search || "");
        const shareId = searchParams.get("shareId");

        if (recalculateResults) {
          setIsCalculating(true);
          const { calculatorDetails, varieties } = data;
          const varietyNames = Object.keys(FINAL_VALUES.varieties);
          const token = await getUserBearerToken();
          const endPoint = shareId
            ? `${process.env.REACT_APP_CALC_API_URL}/v2/kpins/${calculatorDetails.kpin}/shares/${shareId}`
            : `${process.env.REACT_APP_CALC_API_URL}/v2/kpins/${calculatorDetails.kpin}`;


          let resultBody = {
            calculatorDetails: {
              ...FINAL_VALUES.calculatorDetails,
              ...Object.keys(calculatorDetails).reduce((result, key) => {
                let value = calculatorDetails[key];
                if (key.toLowerCase().includes("percentage") && value)
                  value = value / 100;
                if (value === "") {
                  value = 0;
                }
                return {
                  ...result,
                  [key]: value,
                };
              }, {}),
              totalArea: calculatorDetails.totalProductiveArea || 0,
            },
            varieties: varietyNames.reduce((result, varietyName) => {
              return {
                ...result,
                [varietyName]: {
                  ...FINAL_VALUES.varieties[varietyName],
                  ...Object.keys(varieties[varietyName] || {}).reduce(
                    (varietyResult, key) => {
                      let value = varieties[varietyName][key];
                      if (key.toLowerCase().includes("percentage") && value)
                        value = value / 100;
                      if (value === "") value = 0;
                      return {
                        ...varietyResult,
                        [key]: value,
                      };
                    },
                    {}
                  ),
                  //BACKEND expect these values to be at the variety level not calculatorDetails level.
                  compostNitrogenPercent:
                    calculatorDetails.compostNitrogenPercent / 100,
                  compostDryMatter: calculatorDetails.compostDryMatter / 100,
                  manureNitrogenPercent:
                    calculatorDetails.manureNitrogenPercent / 100,
                  manureDryMatter: calculatorDetails.manureDryMatter / 100,
                },
              };
            }, {}),
          };

          //If fuel use is known, ignore self hours
          if(resultBody.calculatorDetails.knowFuelUse === true) {
            resultBody.calculatorDetails.selfSprayingHours = 0;
            resultBody.calculatorDetails.selfMowingHours= 0;
            resultBody.calculatorDetails.selfMulchingHours= 0;
            resultBody.calculatorDetails.selfOtherHours= 0;
            resultBody.calculatorDetails.selfHarvestHours= 0;
          } else {
            resultBody.calculatorDetails.dieselUsed = 0;
          }

          //removed recycling - treat as 0 figure
          resultBody.calculatorDetails.mixedPlasticRecycling = 0;
          
          const response = await fetch(endPoint, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            method: shareId ? "PATCH" : "POST",
            body: JSON.stringify(resultBody),
          });
          const result = await response.json();

          if (result.error) {
            const errorSection = getErrorMessage(result.error);
            const errorMessage = errorSection
              ? `There was an error calculating your results, please check the values in the section labelled ${errorSection}.`
              : `There was an error calclating your results. Please check all your data has been entered correctly`;

            throw new Error(errorMessage);
          }
          setSessionStorage(RESULTS_KEY, result);
          if (!shareId) {
            navigate(
              `../results?shareId=${result.shareId}&kpin=${calculatorDetails.kpin}`
            );
          }
          setRecalculateResults(false);
          setRenewOrchardData(true);
        } else {
          const kpin = searchParams.get("kpin");
          if (!shareId && !kpin) {
            const calcResults = getSessionStorageValue(RESULTS_KEY);
            const { shareId, kpin } = calcResults;
            navigate(`../results?kpin=${kpin}&shareId=${shareId}`);
          }
        }
      } catch (e) {
        setError(e.message);
      } finally {
        setIsCalculating(false);
      }
    };
    generateResults();
    return () => {
      setIsCalculating(false);
      setError(null);
    };
  }, []);

  const getResults = () => {
    const calcResults = getSessionStorageValue(RESULTS_KEY) || {
      results: {},
    };

    if (Object.keys(calcResults.results).length === 0) return {};

    const calculatedResults = {
      ...calcResults,
      results: Object.keys(calcResults.results).reduce((result, key) => {
        const obj = calcResults.results[key];
        return {
          ...result,
          [key]: {
            ...obj,
            emissionsSubtotal: Object.keys(obj).reduce((sum, itemKey) => {
              if (itemKey.includes("nitrogen") || itemKey.includes("total"))
                return sum;
              return sum + obj[itemKey];
            }, 0),
            nitrogenSubtotal: Object.keys(obj).reduce((sum, itemKey) => {
              if (
                !itemKey.includes("nitrogen") ||
                itemKey === "nitrogenPercentageOfEmissions"
              )
                return sum;
              return sum + obj[itemKey];
            }, 0),
          },
        };
      }, {}),
    };

    setSessionStorage(CALCULATED_RESULTS_KEY, calculatedResults);
    return calculatedResults;
  };

  const roundNumber = (value) => {
    return Number(Math.round(value));
  };

  const getTableSubsectionName = (section) => {
    const sectionName = `${section} ${viewResultsBy}`;
    return sectionName
      .replace(/(?:^\w|[A-Z]|\b\w)/g, function (word, index) {
        return index === 0 ? word.toLowerCase() : word.toUpperCase();
      })
      .replace(/\s+/g, "");
  };

  const getRow = (columns, row, data) => {
    const { field, label, isPercentage } = row;
    return {
      label,
      cells: columns.map(({ section, subsection, isSubtotal }) => {
        const subsectionName = isSubtotal
          ? subsection
          : getTableSubsectionName(subsection);
        if (
          !data[section] ||
          !data[section][subsectionName] ||
          !data[section][subsectionName][field]
        )
          return 0;
        let value = data[section][subsectionName][field];
        if (isPercentage) value = value * 100;
        return formatNumber(Math.round(value));
      }),
    };
  };

  const getTableProps = (tableInfo, resultsByLabel) => {
    const calculatorData = getResults();
    const { footers, columns, rows, id } = tableInfo;

    let filteredColumns =
      columns && columns.length
        ? columns.filter((column) => {
            const variety = column.variety;
            if (isNullOrUndefined(variety)) return column;
            const varietyExists = getSavedFieldValue(
              "varieties",
              variety,
              "productiveCanopyArea",
              data
            );
            return varietyExists > 0;
          })
        : columns;

    const formattedRows = rows.map((row) =>
      getRow(filteredColumns, row, calculatorData)
    );

    return {
      headers: columns
        .filter((column) => {
          const variety = column.variety;
          if (isNullOrUndefined(variety)) return column;
          const varietyExists = getSavedFieldValue(
            "varieties",
            variety,
            "productiveCanopyArea",
            data
          );
          return varietyExists > 0;
        })
        .map(({ label }) => {
          return updateGraphLabel(label, resultsByLabel);
        }),
      rows: formattedRows,
      id,
      footers: footers
        ? footers.map((row) => getRow(filteredColumns, row, calculatorData))
        : null,
    };
  };

  const getFertiliserAnalysisTable = () => {
    const calculatorData = getResults();

    if (
      !calculatorData ||
      !calculatorData.results ||
      !calculatorData.results.total
    )
      return {
        ...FERTILISER_ANALYSIS_TABLE,
      };

    const {
      indirectUse: {
        nitrogenNonUrea,
        ureaNitrogen,
        phosphorous,
        potassium,
        sulphur,
        magnesium,
        limeDolomite,
        compost,
        manure,
      },
      fieldEmissions: {
        nitrousOxideUreaWithoutInhibitor,
        nitrousOxideUreaWithInhibitor,
        nitrousOxideNonUrea,
        nitrousOxideCompost,
        carbonDioxideLime,
        carbonDioxideDolomite,
      },
      results: {
        total: { emissionsSubtotal },
      },
    } = calculatorData;

    const fertiliserAnalysisNitrogen =
      ((nitrogenNonUrea +
        ureaNitrogen +
        nitrousOxideUreaWithoutInhibitor +
        nitrousOxideUreaWithInhibitor +
        nitrousOxideNonUrea) /
        emissionsSubtotal) *
        100 || 0;
    const fertiliserAnalysisPotassium =
      (potassium / emissionsSubtotal) * 100 || 0;
    const fertiliserAnalysisMagnesium =
      (magnesium / emissionsSubtotal) * 100 || 0;
    const fertilisterAnalysisSulphur = (sulphur / emissionsSubtotal) * 100 || 0;
    const fertiliserAnalysisPhosphorus =
      (phosphorous / emissionsSubtotal) * 100 || 0;

    const fertiliserAnalysisLimeDolomite =
      ((limeDolomite + carbonDioxideLime + carbonDioxideDolomite) /
        emissionsSubtotal) *
        100 || 0;
    const fertiliserAnalysisCompost =
      ((compost + manure + nitrousOxideCompost) / emissionsSubtotal) * 100 || 0;

    return {
      ...FERTILISER_ANALYSIS_TABLE,
      headers: FERTILISER_ANALYSIS_TABLE.columns.map((column) => column.label),
      rows: [
        {
          label: "",
          cells: ["Nitrogen", fertiliserAnalysisNitrogen.toFixed(2)],
        },
        {
          label: "",
          cells: ["Potassium", fertiliserAnalysisPotassium.toFixed(2)],
        },
        {
          label: "",
          cells: ["Magnesium", fertiliserAnalysisMagnesium.toFixed(2)],
        },
        {
          label: "",
          cells: ["Sulphur", fertilisterAnalysisSulphur.toFixed(2)],
        },
        {
          label: "",
          cells: ["Phosphorus", fertiliserAnalysisPhosphorus.toFixed(2)],
        },
        {
          label: "",
          cells: ["Lime/dolomite", fertiliserAnalysisLimeDolomite.toFixed(2)],
        },
        {
          label: "",
          cells: ["Compost", fertiliserAnalysisCompost.toFixed(2)],
        },
      ],
    };
  };

  const getEnergyAnalysisTable = () => {
    const calculatorData = getResults();

    if (
      !calculatorData ||
      !calculatorData.results ||
      !calculatorData.results.total
    )
      return {
        ...ENERGY_ANALYSIS_TABLE,
      };

    const {
      directEnergyUse: {
        lubricantEmissions,
        contractorDieselEmissions,
        electricityEmissions,
        dieselEmission,
        contractorAviationEmissions,
        petrolEmissions,
      },
      results: {
        total: { emissionsSubtotal },
      },
    } = calculatorData;

    const fuelUseGrower =
      ((lubricantEmissions + dieselEmission + petrolEmissions) /
        emissionsSubtotal) *
        100 || 0;
    const fuelUseContractor =
      ((contractorDieselEmissions + contractorAviationEmissions) /
        emissionsSubtotal) *
        100 || 0;
    const electricity = (electricityEmissions / emissionsSubtotal) * 100 || 0;

    return {
      ...ENERGY_ANALYSIS_TABLE,

      headers: ENERGY_ANALYSIS_TABLE.columns.map((column) => column.label),
      rows: [
        {
          label: "",
          cells: ["Fuel use - grower's", fuelUseGrower.toFixed(2)],
        },
        {
          label: "",
          cells: ["Fuel use - contractor's", fuelUseContractor.toFixed(2)],
        },
        {
          label: "",
          cells: ["Electricity", electricity.toFixed(2)],
        },
      ],
    };
  };

  const getGraphData = (graphType) => {
    const data = getResults();
    const industryAverage = INDUSTRY_AVERAGES[viewResultsBy];
    const { fields, groups } = GRAPH_INFO;
    const userData = fields.reduce((result, graphField) => {
      const subsection =
        graphType.toLowerCase() === "pie"
          ? "total"
          : "totalPer" + `${viewResultsBy.slice(3)}`;
      const { section, field } = graphField;
      let value = 0;
      if (
        data[section] &&
        data[section][subsection] &&
        data[section][subsection][field]
      ) {
        value = data[section][subsection][field];
      }
      return {
        ...result,
        [field]: value,
      };
    }, {});

    const allData = {
      userData,
      industryAverage,
    };

    return groups.map(({ type }) => {
      return fields.map(({ field, label }) => ({
        x: label,
        y: allData[type][field],
      }));
    });
  };

  const getEmissionsEquivalentValues = () => {
    const calculatorResults = getResults();
    if (
      !calculatorResults ||
      !calculatorResults.results ||
      !calculatorResults.results.total ||
      !calculatorResults.results.total.emissionsSubtotal
    )
      return {};
    const { emissionsSubtotal } = calculatorResults.results.total;
    return Object.keys(EMISSIONS_EQUIVALENT_VALUES_INFO).reduce(
      (result, key) => {
        const { divisor } = EMISSIONS_EQUIVALENT_VALUES_INFO[key];
        return {
          ...result,
          [key]: roundNumber(emissionsSubtotal / divisor).toLocaleString(),
        };
      },
      {}
    );
  };

  const handleViewResultsByChange = (value) => {
    const label = value.charAt(3).toLowerCase() + value.slice(4);
    setViewResultsBy(value);
    setViewResultsByLabel(label);
  };

  return (
    <PageContainer>
      <PageHeadingWrapper>
        <Heading2>{getTitle()}</Heading2>
        {search && (
          <ButtonWrapper type="share-bar">
            <PrimaryButton
              onClick={window.print}
              data-testid="share-bar-print-results-button"
            >
              {PRINT_BUTTON_TEXT}
            </PrimaryButton>
          </ButtonWrapper>
        )}
      </PageHeadingWrapper>

      <PageSection>
        <Heading5 type="dont-print">
          Scroll down to see your results in a graph and also benchmarked
          against an industry average.
        </Heading5>
        <SelectInput
          label={VIEW_RESULTS_LABEL_TEXT}
          options={VIEW_RESULTS_OPTIONS}
          value={viewResultsBy}
          onChange={(value) => handleViewResultsByChange(value)}
          id={"view-results"}
        />
      </PageSection>
      <PageSection>
        <Heading5>{EMISSION_CATEGORIES_HEADING_TEXT}</Heading5>
        <DisplayTable
          {...getTableProps(EMISSION_CATEGORIES_TABLE, viewResultsByLabel)}
        />
      </PageSection>
      <PageSection>
        <Heading5>{NITROGEN_FERTILISER_HEADING_TEXT}</Heading5>
        <DisplayTable
          {...getTableProps(NITROGEN_FERTILISER_TABLE, viewResultsByLabel)}
        />
      </PageSection>
      <PageBreak />

      <PageSection>
        <Heading5>
          {FERTILISER_ANALYSIS_OF_TOTAL_EMISSION_HEADING_TEXT}
        </Heading5>
        <DisplayTable {...getFertiliserAnalysisTable()} />
      </PageSection>
      <PageSection>
        <Heading5>{ENERGY_ANALYSIS_OF_TOTAL_EMISSION_HEADING_TEXT}</Heading5>
        <DisplayTable {...getEnergyAnalysisTable()} />
      </PageSection>

      <PageSection>
        <Heading5>{EMISSIONS_AVERAGE_HEADING_TEXT}</Heading5>
        <ParagraphText>{EMISSIONS_AVERAGE_SUBHEADING_TEXT}</ParagraphText>
        <BarGraph
          data={getGraphData("bar")}
          yLabel={updateGraphLabel(GRAPH_INFO.yLabel, viewResultsByLabel)}
          colorScale={[theme.darkBlue, theme.brightGreen]}
          legendLabels={GRAPH_INFO.groups.map((group) => group.label)}
        />
      </PageSection>
      <PageBreak />
      <PageSection>
        <Heading5>{EMISSIONS_PROPORTION_HEADING_TEXT}</Heading5>
        <PieGraph data={getGraphData("pie")} />
      </PageSection>
      {!isCalculating && (
        <PageSection isFullWidth={true}>
          <Heading5>{ANNUAL_EMISSIONS_HEADING_TEXT}</Heading5>
          <EmissionsDisplay {...getEmissionsEquivalentValues()} />
        </PageSection>
      )}
      <ErrorModal error={error} onClose={() => setError(null)} />
      <LoadingModal isOpen={isCalculating} />
    </PageContainer>
  );
}
